/**
 * The jukebox on the lock screen (SPEC-014 PR-14.4).
 *
 * This is the answer to the one real hole in where the control lives. The
 * jukebox's own button is in the app header, and two routes do not wear one: the
 * splash, which is a brand moment with no chrome at all, and a car's page, which
 * is immersive by design and draws artwork to the top edge. A reader who starts
 * a track and then opens a car would, with only the in-app control, have no way
 * to pause without navigating away from what they went to look at.
 *
 * The platform already solved that. Once an `<audio>` element is playing, iOS
 * draws transport controls on the lock screen and in Control Centre, Android
 * draws a notification, and a desktop browser puts them behind the tab's media
 * button — and every one of those reaches this app whether or not the header is
 * on screen. What the Media Session API adds is that they say WHAT is playing
 * rather than the page title, and that the buttons the OS draws are the ones
 * this jukebox actually has.
 *
 * EVERY ENTRY POINT IS SAFE WHERE NONE OF THIS EXISTS. `navigator.mediaSession`
 * is absent in WebKit's Playwright build and in older Firefox; `MediaMetadata`
 * can be missing where the session object is present; and a browser may expose
 * `setActionHandler` and refuse an action it does not support, which throws
 * rather than returning false. All three are ordinary, and the sound plays
 * identically through all of them — there is nothing here the app needs, only
 * things the reader gets when the platform has them.
 */

/**
 * The artwork the OS draws beside the title.
 *
 * The 480 rung, which is what the sheet's own plate uses. A lock screen will
 * happily take a larger picture and this delivery has no larger one; the source
 * cover is 720 square and the build's widest rung is 480, so offering a size
 * nothing wrote would be a URL the OS silently fails to load.
 */
function artworkOf(track) {
    if (!track || !track.cover) {
        return [];
    }

    return [{ src: track.cover.src, sizes: '480x480', type: 'image/webp' }];
}

/**
 * Tell the OS what is playing.
 *
 * `album` is the product rather than a record: what the reader is listening to
 * IS Legends' soundtrack, and a lock screen that said "Unknown album" under a
 * track named after a Countach would look like a file somebody sideloaded.
 */
export function publishNowPlaying({
    track,
    isPlaying,
    albumTitle,
    session = typeof navigator !== 'undefined' ? navigator.mediaSession : null,
    Metadata = typeof window !== 'undefined' ? window.MediaMetadata : null,
} = {}) {
    if (!session) {
        return false;
    }

    try {
        session.playbackState = isPlaying ? 'playing' : 'paused';

        if (Metadata && track) {
            session.metadata = new Metadata({
                title: track.title,
                artist: albumTitle,
                album: albumTitle,
                artwork: artworkOf(track),
            });
        }

        return true;
    } catch (error) {
        // A platform that exposes the object and refuses the write. The audio is
        // unaffected; only the label on somebody's lock screen is.
        return false;
    }
}

/**
 * Wire the OS's transport buttons to this jukebox's, and answer the undo.
 *
 * Each handler is set INDEPENDENTLY inside its own `try`, rather than the four
 * of them together, because a browser that refuses one action throws on it and
 * would otherwise take the other three down with it — which is how a platform
 * with no `previoustrack` ends up with no pause button either.
 */
export function bindMediaSessionActions(handlers, {
    session = typeof navigator !== 'undefined' ? navigator.mediaSession : null,
} = {}) {
    if (!session || typeof session.setActionHandler !== 'function') {
        return () => {};
    }

    const actions = Object.entries(handlers);

    actions.forEach(([action, handler]) => {
        try {
            session.setActionHandler(action, handler);
        } catch (error) {
            // An action this browser does not know about. The OS simply draws
            // one button fewer.
        }
    });

    return () => {
        actions.forEach(([action]) => {
            try {
                session.setActionHandler(action, null);
            } catch (error) {
                // Unbinding what could not be bound is equally harmless.
            }
        });
    };
}
